// Admin workspace > "Diagnostics" tab (admin.ts; platform_admin only — the
// process, its caches and the serving bundle are shared by every client):
//
//   - process memory + per-mtime_cache entry counts / approximate sizes and
//     the serving bundle's version/freshness (GET /api/admin/diagnostics/
//     memory, routers/admin.py — P0 §4.4). Sizes are a deep estimate that
//     can take seconds on big frames, so the first load asks for counts
//     only; "Measure sizes" re-fetches with them.
//   - which engine serves Excel/Word jobs (Office bridge / LibreOffice) and
//     the P5 job worker, from the maintenance status payload
//     (maintenanceUI.ts's loadMaintenanceStatus — shared, not refetched).
//   - "Derived data": precomputed artefacts that are stale or missing
//     (derivedDataUI.ts, GET /api/admin/diagnostics/derived), first, since
//     it is the part someone has to act on.
//
// Viewing as a GIS-view organisation (any public-data council, row #30), the
// maintenance and compute routes are not part of its GIS view
// (config/data_registry.yaml: no gis_view, so the data layer 404s them):
// those two sections say so instead of asking.
import { getJson } from "./api";
import { renderComputeNodesSection } from "./computeNodesUI";
import { isGisOnly } from "./dataProfile";
import { renderDerivedDataSection } from "./derivedDataUI";
import { el } from "./legend";
import { loadMaintenanceStatus, officeSummary } from "./maintenanceUI";
import type { MaintStatus } from "./maintenanceUI";

interface CacheRow {
  name: string;
  entries: number;
  maxsize: number | null;
  family_eviction: boolean;
  family_evictions: number;
  approx_bytes: number | null;
}

interface BundleStatus {
  error?: string;
  client_id?: number;
  target_profile_id?: string | null;
  serving_bundle_id?: number | null;
  profiles_dir?: string;
  serving?: "bundle" | "legacy";
  bundle?: string;
  built_at?: string | null;
  size_bytes?: number | null;
  fresh?: Record<string, boolean>;
  stale_inputs?: string[] | Record<string, unknown>;
}

interface MemoryDiagnostics {
  rss_bytes: number | null;
  rss_mb: number | null;
  cache_entries: number;
  cache_approx_bytes: number | null;
  cache_approx_mb: number | null;
  caches: CacheRow[];
  serving_bundle: BundleStatus;
  note: string;
}

let hostEl: HTMLElement | null = null;
let memory: MemoryDiagnostics | null = null;
let memoryError = "";
let loading = false;

const mb = (b: number | null | undefined): string => (b == null ? "–" : (b / 1048576).toFixed(1));

function scrollWrap(table: HTMLElement): HTMLElement {
  const wrap = el("div", { className: "admin-table-scroll" });
  wrap.append(table);
  return wrap;
}

function redraw(): void {
  if (hostEl) draw(hostEl);
}

async function loadMemory(sizes: boolean): Promise<void> {
  if (loading) return;
  loading = true;
  redraw();
  try {
    memory = await getJson<MemoryDiagnostics>(`/api/admin/diagnostics/memory?sizes=${sizes}`);
    memoryError = "";
  } catch (err) {
    memoryError = `Couldn't load memory diagnostics: ${(err as Error).message}`;
  } finally {
    loading = false;
    redraw();
  }
}

function drawMemory(root: HTMLElement): void {
  root.append(el("h3", {}, "Memory and caches (this web process)"));
  if (memoryError) root.append(el("p", { className: "doc-viewer-error" }, memoryError));
  if (!memory) {
    root.append(el("p", { className: "caption" }, "Loading…"));
    return;
  }
  const m = memory;
  const sized = m.cache_approx_bytes != null;
  root.append(
    el(
      "p",
      { className: "caption" },
      `Resident memory ${m.rss_mb ?? "–"} MB. ${m.cache_entries} cached entries across ${m.caches.length} caches` +
        (sized ? `, about ${m.cache_approx_mb} MB (upper bound).` : " (sizes not measured)."),
    ),
  );
  const sizesBtn = el("button", { disabled: loading }, loading ? "Measuring…" : "Measure sizes");
  sizesBtn.title = "Deep size estimate of every cached value; can take a few seconds";
  sizesBtn.addEventListener("click", () => void loadMemory(true));
  root.append(sizesBtn);

  const t = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  for (const h of ["Cache", "Entries", "Max", "Family evictions", "Approx MB"]) head.append(el("th", {}, h));
  t.append(head);
  for (const c of m.caches) {
    const tr = document.createElement("tr");
    tr.append(
      el("td", { className: "caption" }, c.name),
      el("td", {}, String(c.entries)),
      el("td", { className: "caption" }, c.maxsize == null ? "–" : String(c.maxsize)),
      el("td", { className: "caption" }, c.family_eviction ? String(c.family_evictions) : "–"),
      el("td", { className: "caption" }, sized ? mb(c.approx_bytes) : "–"),
    );
    t.append(tr);
  }
  root.append(scrollWrap(t));
  root.append(el("p", { className: "caption" }, m.note));
}

function drawBundle(root: HTMLElement): void {
  root.append(el("h3", {}, "Serving bundle (client 1's map payloads)"));
  const b = memory?.serving_bundle;
  if (!b) {
    root.append(el("p", { className: "caption" }, memoryError ? "Unavailable." : "Loading…"));
    return;
  }
  if (b.error) {
    root.append(el("p", { className: "doc-viewer-error" }, b.error));
    return;
  }
  if (b.serving !== "bundle") {
    root.append(
      el(
        "p",
        { className: "caption" },
        `Serving legacy (no ready bundle; map payloads come from data/interim). Target profile ${b.target_profile_id ?? "–"}.`,
      ),
    );
    return;
  }
  root.append(
    el(
      "p",
      { className: "caption" },
      `Bundle ${b.bundle} (profile ${b.target_profile_id ?? "–"}), built ${b.built_at ? new Date(b.built_at).toLocaleString() : "–"}, ` +
        `${mb(b.size_bytes)} MB, in ${b.profiles_dir ?? "–"}.`,
    ),
  );
  const fresh = Object.entries(b.fresh ?? {});
  if (fresh.length) {
    const t = el("table", { className: "detail-table" });
    const head = document.createElement("tr");
    head.append(el("th", {}, "Group"), el("th", {}, "Fresh"));
    t.append(head);
    for (const [group, ok] of fresh) {
      const tr = document.createElement("tr");
      tr.append(el("td", {}, group), el("td", { className: ok ? "caption" : "doc-viewer-error" }, ok ? "yes" : "stale"));
      t.append(tr);
    }
    root.append(scrollWrap(t));
  }
  const stale = b.stale_inputs;
  const staleList = Array.isArray(stale) ? stale : stale ? Object.keys(stale) : [];
  if (staleList.length) {
    root.append(el("p", { className: "doc-viewer-error" }, `Inputs newer than the bundle: ${staleList.join(", ")}`));
  }
}

function drawEngines(root: HTMLElement, s: MaintStatus | null): void {
  root.append(el("h3", {}, "Office engines and job worker"));
  if (!s) {
    root.append(el("p", { className: "caption" }, "Loading…"));
    return;
  }
  root.append(el("p", { className: "caption" }, s.office ? officeSummary(s.office) : "Office engine status unavailable."));
  const w = s.job_worker;
  root.append(
    el(
      "p",
      { className: w && w.workers.length === 0 ? "doc-viewer-error" : "caption" },
      w
        ? `Job worker queue "${w.queue}": ${w.workers.length} live worker(s), ${w.active} active top-level job(s).` +
            (w.workers.length === 0 ? " Module runs, approved re-runs and the replacement strategy stay queued." : "")
        : "Job worker status unavailable.",
    ),
  );
}

function draw(root: HTMLElement): void {
  root.innerHTML = "";
  const header = el("div", { className: "admin-pane-header" });
  header.append(el("h2", {}, "Diagnostics"));
  const refresh = el("button", { disabled: loading }, "Refresh");
  refresh.addEventListener("click", () => {
    void loadMemory(memory?.cache_approx_bytes != null);
    if (!isGisOnly()) loadMaintenanceStatus(redraw, true);
  });
  header.append(refresh);
  root.append(header);
  root.append(
    el(
      "p",
      { className: "caption" },
      "Shared infrastructure serving every organisation (platform admins only). Figures are this server process's own.",
    ),
  );
  // derived-data freshness (derivedDataUI.ts): its own state, polling and re-check
  const derived = el("div", { className: "derived-data-section" });
  root.append(derived);
  renderDerivedDataSection(derived);
  drawMemory(root);
  drawBundle(root);
  if (isGisOnly()) {
    root.append(el("h3", {}, "Office engines, job worker and compute nodes"));
    root.append(
      el(
        "p",
        { className: "caption" },
        "Not available while viewing a GIS-view organisation: maintenance and compute are not part of its data. " +
          "Switch back to your own organisation to see them.",
      ),
    );
    return;
  }
  drawEngines(root, loadMaintenanceStatus(redraw));
  // remote compute nodes (computeNodesUI.ts): its own state and refresh
  const compute = el("div", { className: "compute-nodes-section" });
  root.append(compute);
  renderComputeNodesSection(compute);
}

/** Renders into the Diagnostics tab; fetches on first render. */
export function renderDiagnosticsSection(root: HTMLElement): void {
  hostEl = root;
  draw(root);
  if (memory === null) void loadMemory(false);
}
